import { useState } from "react";
import { Avatar, Flex, Typography } from "antd";
import { Button } from "@libertify/ui";
import { PencilSimpleIcon, TrashIcon } from "@phosphor-icons/react";
import type { InsightComment as InsightCommentType } from "../../types";
import { useTranslate } from "#src/hooks";
import { useSession } from "@/hooks/useSession";
import { getInitials } from "@/utils/text";
import { DeleteButton } from "components/DeleteButton/";
import { InsightComment } from "./InsightComment";

export type InsightCommentsListProps = {
  comments: InsightCommentType[];
  isRemoving: boolean;
  editComment: (
    commentId: string,
    body: string,
    onSuccess?: () => void
  ) => void;
  removeComment: (commentId: string, onSuccess?: () => void) => void;
};

export const InsightCommentsList = ({
  comments,
  isRemoving,
  editComment,
  removeComment,
}: InsightCommentsListProps) => {
  const t = useTranslate();
  const { user } = useSession();
  const [editingId, setEditingId] = useState<string | null>(null);

  if (comments.length === 0)
    return (
      <Typography.Text
        type="secondary"
        italic
        style={{ textAlign: "center", padding: "12px 0" }}
      >
        {t("media.analytics.insights.noComments")}
      </Typography.Text>
    );

  return (
    <Flex vertical gap={8}>
      {comments.map((comment) => {
        const canManage = Boolean(comment.id) && comment.author_id === user?.id;
        const isEditingThis = editingId === comment.id;

        return (
          <Flex key={comment.id ?? comment.created_at} gap={10}>
            <Avatar style={{ width: 30, height: 30 }}>
              {getInitials(comment.author_first_name, comment.author_last_name)}
            </Avatar>
            <Flex gap={8} align="flex-start" style={{ flex: 1 }}>
              <InsightComment
                comment={comment}
                isEditing={isEditingThis}
                onSaveEdit={(body) =>
                  comment.id &&
                  editComment(comment.id, body, () => setEditingId(null))
                }
                onCancelEdit={() => setEditingId(null)}
              />
              {canManage && !isEditingThis && (
                <Flex gap={2} align="center">
                  <Button
                    variant="tertiary"
                    size="s"
                    aria-label={t("media.analytics.insights.editComment")}
                    icon={<PencilSimpleIcon size={14} />}
                    onClick={() => setEditingId(comment.id ?? null)}
                  />
                  <DeleteButton
                    confirmTitle={t(
                      "media.analytics.insights.deleteCommentConfirmTitle"
                    )}
                    loading={isRemoving}
                    onDelete={() => removeComment(comment.id!)}
                    icon={<TrashIcon />}
                  />
                </Flex>
              )}
            </Flex>
          </Flex>
        );
      })}
    </Flex>
  );
};
